import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from "react";
import { getMultiplePropertyRPT } from "./property-provider";
import { useGrpcClient } from "../lib/grpc";
import { InventoryAPIPromiseClient } from "../gen/witbooking/backoffice/inventory/v1/inventory_api_grpc_web_pb";
import { useKeycloakAuthz } from "./keycloak-provider";
import {
  GetInventoryLinesRequest,
  GetInventoryLinesResponse,
} from "../gen/witbooking/backoffice/inventory/v1/inventory_api_pb";
import { getLanguageFromFullLocale } from "../lib/config";
import { useIntl } from "react-intl";
import { InventoryEntry } from "../lib/model";

interface IPropertyInventories {
  propertyTicker: string;
  inventoryTickers: InventoryEntry[] | undefined | null;
}

type InventoryData = IPropertyInventories[] | undefined | null;

const Context = createContext<InventoryData>(undefined);

interface PropertyInventoriesProviderProps {
  propertyTickers: string[];
}

export function PropertyInventoriesProvider({
  propertyTickers,
  children,
}: PropsWithChildren<PropertyInventoriesProviderProps>) {
  const [data, setData] = useState<InventoryData>([]);

  const keycloakAuthz = useKeycloakAuthz();
  const locale = useIntl().locale;

  const inventoryClient = useGrpcClient(InventoryAPIPromiseClient);
  useEffect(() => {
    async function load() {
      try {
        const propertyRPTList = await getMultiplePropertyRPT(propertyTickers, keycloakAuthz);
        const inventoryRequest = new GetInventoryLinesRequest();
        inventoryRequest.setLocale(getLanguageFromFullLocale(locale));
        inventoryRequest.setOnlyValidLines(true);
        if (Array.isArray(propertyRPTList)) {
          const inventoriesPromises: Promise<GetInventoryLinesResponse>[] = propertyRPTList.map(
            ({ token }) => {
              const finalMeta = {
                Authorization: `Bearer ${token}`,
              };
              return inventoryClient.getInventoryLines(inventoryRequest, finalMeta);
            }
          );
          const inventories = await Promise.all(inventoriesPromises);

          const data = inventories.map((inventory, index) => ({
            propertyTicker: propertyRPTList[index].propertyTicker,
            inventoryTickers: inventory
              .toObject()
              .inventoryLinesList.map(inventoryLine => ({
                accommodation: inventoryLine.accommodation!!,
                condition: inventoryLine.condition!!,
                configuration: inventoryLine.configuration!!,
                mealPlan: inventoryLine.mealPlan!!,
                ticker: inventoryLine.code!!,
                legacyId: inventoryLine.legacyId!!,
              }))
          }));
          setData(data);
        }
      } catch (e) {
        console.error("Error retrieving inventory lines", e);
        return;
      }
    }
    if (propertyTickers.length) {
      load();
    }
  }, [inventoryClient, keycloakAuthz, locale, propertyTickers]);

  return <Context.Provider value={data}>{children}</Context.Provider>;
}

export function usePropertyInventoryData(): InventoryData {
  return useContext(Context);
}
